import { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import { useWebsites } from '@/context/WebsitesContext';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import IdeaCards, { PostIdea as Idea } from '@/components/chat/IdeaCards';
import PostDraftCard from '@/components/chat/PostDraftCard';
import { extractIdeasStrict, extractPostStrict, extractPostLoose } from '@/utils/chatContentParsers';
import { ScrollArea } from '@/components/ui/scroll-area';
import { useIsMobile } from '@/hooks/use-mobile';
import { addToCalendar } from '@/utils/duplicatePrevention';
import { parseChatMessage, renderCardContent } from '@/utils/chatCardParsingHelpers';
import { useSharedChatLogic } from '@/hooks/useSharedChatLogic';
import OnboardingSidebarChat from '@/components/onboarding/OnboardingSidebarChat';
import type { OnboardingStep } from '@/types/onbaording.types';
import CalendarDrawer, { CalendarItem } from '@/components/CalendarDrawer';
import WritePostDrawer from '@/components/WritePostDrawer';
import { Send, Plus, Mic, Calendar as CalendarIcon } from 'lucide-react';
import { contentSteps, chatSteps, settingsSteps } from '@/data/onboarding.data';
import ReactMarkdown from 'react-markdown';
import { useOnboarding } from '@/context/OnboardingContext';
import useOnboardingSetupAndInitializationChat from '@/hooks/useOnboardingSetupAndInitializationChat';
import SignupDialog from '@/components/dialogs/SignupDialog';
import OnboardingChatDesktop from '@/components/onboarding/OnboardingChatDesktop';
import config from '@/config';

// Helper function to format titles with proper Danish capitalization
const formatDanishTitle = (title: string): string => {
  // Danish titles only capitalize the first word and proper nouns
  // This is a simplified approach without proper noun detection

  // Split the title into words
  const words = title.split(" ");

  // Capitalize first word
  if (words.length > 0) {
    words[0] =
      words[0].charAt(0).toUpperCase() + words[0].slice(1).toLowerCase();
  }

  // Keep rest of words lowercase, except potential proper nouns
  // For proper implementation, this would need NLP to detect proper nouns
  for (let i = 1; i < words.length; i++) {
    // Simple heuristic: keep words that were originally capitalized as capitalized
    // This isn't perfect but helps retain proper nouns
    if (words[i].length > 0 && words[i][0] === words[i][0].toUpperCase()) {
      // This might be a proper noun, keep it capitalized
      continue;
    }
    words[i] = words[i].toLowerCase();
  }

  return words.join(" ");
};

// Helper function to generate a digest from full content
const generateDigest = (content: string, maxLength: number = 300): string => {
  if (!content) return '';
  
  // Remove HTML tags and get plain text
  const plainText = content.replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim();
  
  // If content is short enough, return as is
  if (plainText.length <= maxLength) return plainText;
  
  // Find the last complete sentence within the limit
  const truncated = plainText.substring(0, maxLength);
  const lastSentenceEnd = truncated.lastIndexOf('.');
  
  if (lastSentenceEnd > maxLength * 0.5) {
    return truncated.substring(0, lastSentenceEnd + 1);
  }
  
  // If no good sentence break, truncate at word boundary
  const lastSpace = truncated.lastIndexOf(' ');
  return truncated.substring(0, lastSpace) + '...';
};

type ChatMessage = { 
  role: 'assistant' | 'user'; 
  content: string;
  _id?: number;
  _streaming?: boolean;
  _typing?: boolean;
  _magic?: string;
  _loading?: boolean;
  _mode?: 'unknown' | 'chat' | 'idea' | 'write';
};

type PostIdea = Idea;

function ChatIdeaCard({ idea, onAddToCalendar }: { idea: PostIdea; onAddToCalendar?: (idea: PostIdea) => void }) {
  return (
    <div className="rounded-xl border bg-background p-4 shadow-sm hover:shadow-md transition-shadow">
      <div className="space-y-3">
        <div>
          <h4 className="text-sm font-semibold text-foreground leading-tight">{idea.title}</h4>
          {idea.digest ? (
            <p className="mt-2 text-sm text-muted-foreground leading-relaxed">{idea.digest}</p>
          ) : null}
        </div>
        <div className="pt-2">
          <Button size="sm" variant="default" className="h-8 px-3 text-xs w-full"
            onClick={() => onAddToCalendar?.(idea)}
          >
            <CalendarIcon className="h-3 w-3 mr-1" />
            Add to calendar
          </Button>
        </div>
      </div>
    </div>
  );
}

export default function OnboardingChat() {
  // Get url from query params
  const url = new URLSearchParams(window.location.search).get('url');
  const { currentWebsite } = useWebsites();
  const { state, setState, setupStarted } = useOnboarding();
  const { handleStartSetup1 } = useOnboardingSetupAndInitializationChat();
  const isMobile = useIsMobile();
  const [messages, setMessages] = useState<ChatMessage[]>(() => [{
    role: 'assistant',
    content: "This chat is trained on your website. Ask for ideas, outlines, or ready‑to‑publish posts, then add chosen items straight to your content plan. [Sign up for free](signup) to save work, collaborate, and help it get smarter with your preferences."
  }]);
  // Shared chat logic with language detection enabled
  const { checkLanguageDetection, setupLanguageDetection, sendMessage: sharedSendMessage, handleCreateOutline: sharedHandleCreateOutline } = useSharedChatLogic({
    currentWebsite,
    websiteId: currentWebsite?.id || '',
    enableLanguageDetection: true
  });
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [writeDrawerOpen, setWriteDrawerOpen] = useState(false);
  const [writeDrawerData, setWriteDrawerData] = useState<{ subject_matter: string; post_content: string; post_digest?: string } | null>(null);
  const [calendarItems, setCalendarItems] = useState<CalendarItem[]>(() => {
    try { return JSON.parse(localStorage.getItem('calendar_items') || '[]'); } catch { return []; }
  });
  const debounceRef = useRef<NodeJS.Timeout | null>(null);
  
  // Debug calendar drawer state
  useEffect(() => {
    console.log('Calendar drawer state changed:', drawerOpen);
  }, [drawerOpen]);
  
  // Debug calendar items state
  useEffect(() => {
    console.log('Calendar items changed:', calendarItems);
  }, [calendarItems]);

  // Use settings names if analysis is complete (step >= 2)
  const displaySteps = (typeof state.step === 'number' && state.step >= 2) ? settingsSteps : chatSteps;

  // Initialise website URL from ?url or #hash
  useEffect(() => {
    try {
      const params = new URLSearchParams(window.location.search);
      const urlParam = params.get('url');
      const hashValue = (window.location.hash || '').replace(/^#/, '').trim();
      const candidate = urlParam || (hashValue && !hashValue.includes('=/') ? hashValue : '');
      if (!candidate) return;

      let decoded = candidate;
      try {
        decoded = decodeURIComponent(candidate);
        if (decoded.includes('%')) {
          try { decoded = decodeURIComponent(decoded); } catch {}
        }
      } catch {}

      decoded = decoded.replace(/^https?:\/\/https?:\/\//, 'https://');
      decoded = decoded.replace(/^https?:\/\/https%3A%2F%2F/, 'https://');
      if (!/^https?:\/\//i.test(decoded)) {
        decoded = 'https://' + decoded;
      }
      try {
        const u = new URL(decoded);
        decoded = u.origin;
      } catch {}

      localStorage.setItem('onboardingWebsite', decoded);
      setState((prev) => ({ ...prev, websiteUrl: decoded }));
    } catch {}
  }, [setState]);

  // Start the onboarding setup process when component mounts
  useEffect(() => {
    console.log('OnboardingChat useEffect - setupStarted:', setupStarted.current, 'websiteUrl:', state.websiteUrl);
    if (!setupStarted.current && state.websiteUrl) {
      console.log('Starting chat onboarding setup for:', state.websiteUrl);
      handleStartSetup1();
    }
  }, [setupStarted, state.websiteUrl, handleStartSetup1]);

  const scrollRef = useRef<HTMLDivElement>(null);
  const endRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    // Try viewport scroll first
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: 'smooth' });
    // Fallback: scroll the bottom sentinel into view
    endRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' });
  }, [messages]);

  // Check for language detection
  useEffect(() => {
    checkLanguageDetection(setMessages);
  }, [checkLanguageDetection]);

  // Setup language detection events
  useEffect(() => {
    return setupLanguageDetection(setMessages);
  }, [setupLanguageDetection]);

  const handleCreateOutline = async (idea: PostIdea) => {
    await sharedHandleCreateOutline(idea, messages, setMessages, setInput, setSending);
  };



  const send = async () => {
    if (!input.trim()) return;
    const text = input.trim();
    setInput('');
    await sharedSendMessage(text, messages, setMessages, setSending);
  };


  // Function to trigger signup modal
  const handleSignupClick = () => {
    setState((prev) => ({ ...prev, showSignupModal: true }));
  };

  const persistMessagesToDb = async () => {
    let parsedContentList = [];
    let parsedIdeasList = [];
    
    for (let index = 0; index < messages.length; index++) {
      const message = messages[index];
    
      if (!message?.content?.startsWith('{"_type"')) {
        continue;
      }
    
      const parsed = JSON.parse(message?.content);
    
      if (parsed && parsed?._type === 'post_draft') {
        parsedContentList.push(parsed);
      } else if (parsed?._type === 'ideas') {
        parsedIdeasList.push(parsed);
      }
    }

    const websiteId = localStorage.getItem('website_id');

    const { data, error } = await supabase
      .from('onboarding')
      .upsert({
        website_id: websiteId,
        website_url: url,
        chat_summary: messages,
        post_theme_content: parsedContentList,
        post_theme_suggestions: parsedIdeasList
      }, {
        onConflict: 'website_id'
      })

    if (error) {
      console.error('Error persisting messages to db:', error);
    } else {
      console.log('Messages persisted to db successfully:', data);
    }
  }

  const debouncedPersistMessagesToDb = useCallback(() => {
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }
    debounceRef.current = setTimeout(() => {
      persistMessagesToDb();
    }, 2000);
  }, [messages]);

  const createNewWebsite = async () => {
    let websiteId = null;

    const { data: onboardingData, error: onboardingError } = await supabase
      .from('onboarding')
      .select('*')
      .eq('website_url', url)
      .single();

    if (onboardingData?.website_id) {
      websiteId = onboardingData.website_id;
    } else {
      websiteId = crypto.randomUUID();
    }

    localStorage.setItem('website_id', websiteId);

    await fetch(`${config.supabase.functionsUrl}/store-onboarding-analytics`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`
      },
      body: JSON.stringify({
        website_id: websiteId,
        website_url: url,
        status: 'started'
      })
    });
  }

  /**
   * Create new website
   */
  useEffect(() => {
    createNewWebsite();
  }, [])
  
  /**
   * Persist messages to db
   */
  useEffect(() => {
    debouncedPersistMessagesToDb();
  }, [messages])

  // Single responsive layout
  return (
    <OnboardingChatDesktop
      messages={messages}
      input={input}
      setInput={setInput}
      sending={sending}
      onSend={send}
      onSignupClick={handleSignupClick}
      displaySteps={displaySteps}
      drawerOpen={drawerOpen}
      setDrawerOpen={setDrawerOpen}
      calendarItems={calendarItems}
      setCalendarItems={setCalendarItems}
      writeDrawerOpen={writeDrawerOpen}
      setWriteDrawerOpen={setWriteDrawerOpen}
      writeDrawerData={writeDrawerData}
      setWriteDrawerData={setWriteDrawerData}
      handleCreateOutline={handleCreateOutline}
      generateDigest={generateDigest}
      scrollRef={scrollRef}
      currentWebsite={currentWebsite}
    />
  );
}


